/**
 * ScholarSync — Checkout Success Page (Pay-First Flow)
 * File: frontend/src/CheckoutSuccess.js
 *
 * After Stripe payment, this page:
 *   Step 1 — Verifies payment via checkout-complete API
 *   Step 2 — Prompts the parent to create a password (if new user)
 *   Step 3 — Shows "Set Up Your Child's Profile" CTA
 *
 * Also handles:
 *   - Returning customers (already have a password → skip to step 3)
 *   - Backup email links with ?token=xxx (skip API call, go to password form)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from './config';
import { useAuth } from './AuthContext';

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  // Flow state: verifying → password → confirmed → error
  const [step, setStep] = useState('verifying');
    const [error, setError] = useState(''); // eslint-disable-line no-unused-vars

  // Data from checkout-complete API
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [passwordToken, setPasswordToken] = useState('');

  // Password form
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const tokenParam = searchParams.get('token');

    // Backup email link: ?token=xxx → skip API call, show password form
    if (tokenParam) {
      setPasswordToken(tokenParam);
      setStep('password');
      return;
    }

    if (!sessionId) {
      setStep('confirmed'); // No session_id — just show the success page
      return;
    }

    // Call checkout-complete API
    const verify = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/stripe/checkout-complete?session_id=${encodeURIComponent(sessionId)}`
        );
        const data = await res.json();

        if (!res.ok) {
          // If verification fails, still show success — Stripe redirect means payment worked
          console.warn('checkout-complete error:', data.error);
          setStep('confirmed');
          return;
        }

        if (data.needsPassword === false) {
          // Returning customer — auto-login and skip to confirmed
          if (data.token && data.user) {
            login(data.token, data.user);
          }
          setStep('confirmed');
        } else {
          // New user — show password form
          setEmail(data.email || '');
          setFirstName(data.firstName || '');
          setPasswordToken(data.passwordToken || '');
          setStep('password');
        }
      } catch (err) {
        console.error('checkout-complete fetch error:', err);
        // Payment succeeded (Stripe redirected us) — show success anyway
        setStep('confirmed');
      }
    };

    // Small delay to give the webhook time to fire
    const timer = setTimeout(verify, 1500);
    return () => clearTimeout(timer);
  }, [searchParams, login]);

  // ── Handle password submission ──
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setSaving(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/set-initial-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: passwordToken, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setPasswordError(data.error || 'Failed to set password. Please try again.');
        setSaving(false);
        return;
      }

      // Auto-login with the returned JWT
      if (data.token && data.user) {
        login(data.token, data.user);
      }

      setStep('confirmed');
    } catch (err) {
      setPasswordError('Connection error. Please try again.');
      setSaving(false);
    }
  };

  // ═══════════════ RENDER ═══════════════

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* ── STEP: Verifying ── */}
        {step === 'verifying' && (
          <>
            <div style={styles.icon}>⏳</div>
            <h1 style={styles.heading}>Confirming your payment...</h1>
            <p style={styles.sub}>This will only take a moment.</p>
          </>
        )}

        {/* ── STEP: Set Password ── */}
        {step === 'password' && (
          <>
            <div style={styles.icon}>🎉</div>
            <h1 style={styles.heading}>
              {firstName ? `Welcome, ${firstName}!` : 'Welcome to Your Scholarship Finder!'}
            </h1>
            <p style={styles.sub}>
              Your payment was successful. Create a password to secure your account.
            </p>

            {email && (
              <div style={styles.emailDisplay}>
                <span style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Your account email</span>
                <span style={{ fontWeight: 600 }}>{email}</span>
              </div>
            )}

            {passwordError && <div style={styles.errorBox}>{passwordError}</div>}

            <form onSubmit={handleSetPassword}>
              <div style={styles.field}>
                <label style={styles.label}>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={styles.input}
                  placeholder="At least 8 characters"
                  autoFocus
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={styles.input}
                  placeholder="Re-enter your password"
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                style={{
                  ...styles.btnPrimary,
                  opacity: saving ? 0.6 : 1,
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Setting up your account...' : 'Create Password & Continue →'}
              </button>
            </form>
          </>
        )}

        {/* ── STEP: Confirmed ── */}
        {step === 'confirmed' && (
          <>
            <div style={styles.icon}>🎉</div>
            <h1 style={styles.heading}>You're all set!</h1>
            <p style={styles.sub}>
              Your payment was successful and your account is ready.
              You now have full access to AI-powered scholarship matching.
            </p>

            <div style={styles.nextSteps}>
              <h3 style={{ margin: '0 0 12px', color: '#166534', fontSize: 16 }}>
                What's next?
              </h3>
              <ol style={{ margin: 0, paddingLeft: 20, color: '#444', lineHeight: 2 }}>
                <li>Create your child's profile</li>
                <li>Our AI matches them with scholarships they qualify for</li>
                <li>Track applications and deadlines from your dashboard</li>
              </ol>
            </div>

            <button
              onClick={() => navigate('/parent/link-student')}
              style={styles.btnPrimary}
            >
              Set Up Your Child's Profile →
            </button>

            <button
              onClick={() => navigate('/parent/dashboard')}
              style={styles.btnGhost}
            >
              Go to Dashboard
            </button>
          </>
        )}

        {/* ── STEP: Error ── */}
        {step === 'error' && (
          <>
            <div style={styles.icon}>⚠️</div>
            <h1 style={styles.heading}>Something went wrong</h1>
            <p style={styles.sub}>{error || 'Please contact us at theteam@yourscholarshipfinder.com'}</p>
            <button onClick={() => navigate('/')} style={styles.btnPrimary}>
              Return to Home
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Styles ──
const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px 20px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '48px 40px',
    maxWidth: 520,
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
  },
  icon: { fontSize: 48, marginBottom: 16 },
  heading: { color: '#1a1a2e', marginBottom: 12, fontSize: 24 },
  sub: { color: '#555', fontSize: 16, lineHeight: 1.6, marginBottom: 24 },
  emailDisplay: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 20,
    textAlign: 'left',
  },
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: 12,
    color: '#dc2626',
    fontSize: 13,
    marginBottom: 16,
    textAlign: 'left',
  },
  field: { marginBottom: 16, textAlign: 'left' },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  btnPrimary: {
    width: '100%',
    padding: '14px 36px',
    background: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 17,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  btnGhost: {
    width: '100%',
    background: 'transparent',
    color: '#666',
    border: 'none',
    padding: 12,
    fontSize: 14,
    cursor: 'pointer',
    marginTop: 8,
  },
  nextSteps: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 12,
    padding: '20px 24px',
    marginBottom: 24,
    textAlign: 'left',
  },
};
